// apple-login.service.ts
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnInit } from '@angular/core';
import { LoginApiService } from './login-api.service';
import { firstValueFrom } from 'rxjs';
import { StorageUtils } from '../utils/storage.util';
import { environment } from '@env';

declare global {
  interface Window { AppleID: any; }
}

@Injectable({ providedIn: 'root' })
export class AppleLoginService {
  private http = inject(HttpClient);
  private apiService = inject(LoginApiService);
  private storageUtils = inject(StorageUtils);

  private codeVerifier = '';
  private codeChallenge = '';

  private loaded = false;

  private baseUrl = environment.baseUrl;
  private clientId = environment.oidcApple.clientId;
  private redirectUrl = environment.oidcApple.redirectUrl;

  async init(): Promise<void> {
    const storageData = this.storageUtils.getStVerifierChallenge();

    this.codeVerifier = storageData.verifier !== null && storageData.verifier.length > 0 ?
     storageData.verifier : Math.random().toString(36).substring(2, 15);

    if (storageData.codeChallenge !== null && storageData.codeChallenge.length > 0) {
      this.codeChallenge = storageData.codeChallenge;
    } else {
      const buffer = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(this.codeVerifier)
      );
      this.codeChallenge = this.base64urlEncode(buffer);
    }

  }

  private loadSdk(): Promise<void> {
    if (this.loaded) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      s.onload = () => { this.loaded = true; resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  async initAppleLogin(params: NonceAndState) {
    await this.loadSdk();
    await window.AppleID.auth.init({
      clientId: this.clientId,
      scope: 'name email',
      redirectURI: this.redirectUrl,
      usePopup: false,
      nonce: params.nonce,
      state: params.state
    });
  }

  private async getNonceAndState(): Promise<NonceAndState> {
    return await firstValueFrom(this.http.get<NonceAndState>(this.baseUrl+"/oidc/apple/start"));
  }

  public async appleLogin(): Promise<TAppleLoginState> {
    // call auth, if it fails because of 401 as expected, start login flow
    this.init();
    try {
      const authorizedRes = await this.getAuthApiCall();
      if (!authorizedRes) {
        return await this.appleLoginFlow();
      }
      return 'SUCCESS';
    } catch (error) {
      return await this.appleLoginFlow();
    }
  }

  private async appleLoginFlow(): Promise<TAppleLoginState> {
    try {
      let params = await this.getNonceAndState();
      await this.initAppleLogin(params);

      this.storageUtils.setStVerifierChallenge(this.codeVerifier, this.codeChallenge);

      //call apple sign in
      await window.AppleID.auth.signIn();

      return 'SUCCESS';
    } catch (error) {
      return 'ERROR';
    }
  }

  private base64urlEncode(buffer: any) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private async getAuthApiCall(): Promise<string> {
    return this.apiService.getAuthApiCall(this.codeChallenge);
  }

  private deleteAllCookies() {
    document.cookie.split(';').forEach(function (cookie) {
      const name = cookie.split('=')[0].trim();
      document.cookie =
        name + '=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    });
  }

  private async getAuthToken(code: string): Promise<TAppleLoginState> {
    const bearerTokenResp = await this.apiService.getAuthToken(
      code,
      this.codeVerifier
    );

    if (bearerTokenResp.access_token) {
      window.localStorage.setItem('accessToken', bearerTokenResp.access_token);
      return 'SUCCESS';
    } else {
      return 'ERROR';
    }
  }

  public async completeAppleLogin(): Promise<TAppleLoginState> {
    this.init();
    // fetch auth code
    const authCode = await this.getAuthApiCall();
    // delete all cookies as on beginning of the process
    this.deleteAllCookies();
    // get accessToken
    let state = await this.getAuthToken(authCode);

    //delete verifier and challenge zo sessionStorage
    this.storageUtils.removeStVerifierChallenge();

    return state;
  }

}

export type TAppleLoginState =
  'SUCCESS'
  | 'ERROR'
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'DISABLED';

export class NonceAndState {
  nonce!: string;
  state!: string;
}
